import {
  ExclamationTriangleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { CircleX, KeyRound, Mail } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { IconButton } from '@/components/IconButton';
import { Modal } from '@/components/Modal';
import { OverflowText } from '@/components/OverflowText';
import {
  getColumnWidth,
  TableVariant,
} from '@/components/table/columns/registry';
import { TanStackTable } from '@/components/table/TanStackTable';
import { Columns } from '@/components/table/types';
import { Tooltip } from '@/components/Tooltip';
import { useModifyAccount } from '@/hooks';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { useRbacEnabled } from '@/hooks/useRbacEnabled';
import { queryClient } from '@/queryclient';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { Account, AccountMetadata } from '@/types';
import { formatDate } from '@/utils/date.util';
import { formatRoleName } from '@/utils/role.util';
import { getAuthMethod } from '@/utils/userDisplay.util';

// Extended Account type that includes access activity from Access records
interface AccountWithAccess extends Account {
  access?: string; // Last activity timestamp from Access record
}

const DATE_FORMAT = 'yyyy-MM-dd HH:mm' as const;

const isAccount = (account: AccountWithAccess): boolean => {
  return account?.key === 'self';
};

interface AuthorizedUsersTableProps {
  users: AccountWithAccess[];
  tableName: string;
  title?: string;
  description?: string;
  error?: Error | null;
  status: 'error' | 'success' | 'pending';
  customerEmail?: string;
  showHeader?: boolean;
  onUserRevoked?: () => void;
  readOnly?: boolean;
  ssoDomains?: string[];
  variant?: TableVariant;
}

const AuthorizedUsersTableBase: React.FC<AuthorizedUsersTableProps> = ({
  users,
  tableName,
  title,
  description,
  error,
  status,
  customerEmail,
  showHeader = true,
  onUserRevoked,
  readOnly = false,
  ssoDomains = [],
  variant = 'default',
}) => {
  const navigate = useNavigate();
  const { setLastVisitedUserId } = useGlobalState();
  const { me: primaryEmail } = useAuth();
  const { mutateAsync: unlink } = useModifyAccount('unlink', customerEmail);
  const { mutateAsync: reinvite } = useModifyAccount('link', customerEmail);
  const [showRevokeAuthorization, setShowRevokeAuthorization] = useState(false);
  const [unlinkUser, setUnlinkUser] = useState<{
    username: string;
    config: AccountMetadata;
  } | null>(null);
  const [isReinviting, setIsReinviting] = useState<string | null>(null);
  const enableRbac = useRbacEnabled();

  const handleReinvite = async (account: AccountWithAccess) => {
    setIsReinviting(account.member);
    try {
      await reinvite({
        username: account.member,
        config: account.config,
        silent: true, // Suppress automatic "Account connected" toast from hook
      });
      toast.success('Invitation sent', {
        description: `Re-invite email sent to ${account.member}`,
      });
    } catch (error) {
      // Error toast already handled by useModifyAccount hook
      console.error('Failed to re-invite user:', error);
    } finally {
      setIsReinviting(null);
    }
  };

  // Build columns array with conditional columns based on variant
  // Variant determines both column visibility AND column widths
  const baseColumns: Columns<AccountWithAccess> = [
    {
      label: 'User',
      id: 'member',
      ...getColumnWidth('account', 'displayName', variant),
      cell: account =>
        isAccount(account) ? (
          <span className="text-disabled cursor-not-allowed pt-2">
            <OverflowText text={account?.config?.displayName ?? account.name} />
          </span>
        ) : (
          <OverflowText text={account?.config?.displayName ?? account.member} />
        ),
    },
  ];

  // Add Role column for default variant always, or compact variant when RBAC is enabled
  if (variant !== 'compact' || enableRbac) {
    baseColumns.push({
      label: 'Role',
      id: '',
      ...getColumnWidth('account', 'role', variant),
      cell: (account: AccountWithAccess) => {
        if (!enableRbac) {
          return isAccount(account) ? (
            <span className="text-disabled cursor-not-allowed">Account</span>
          ) : (
            'Collaborator'
          );
        }

        // RBAC enabled: show actual role
        if (isAccount(account)) {
          return (
            <span className="text-disabled cursor-not-allowed">Admin</span>
          );
        }
        return formatRoleName(account.role);
      },
    });
  }

  // Add Authentication Method column (always shown in both variants)
  baseColumns.push({
    label: variant === 'compact' ? 'Auth Method' : 'Authentication Method',
    id: '',
    ...getColumnWidth('account', 'authMethod', variant),
    cell: (account: AccountWithAccess) => {
      const authMethod = getAuthMethod(account.member, ssoDomains);
      const isSSO = authMethod === 'sso';

      return (
        <div className="flex items-center gap-2">
          {isSSO ? (
            <>
              <ShieldCheckIcon className="size-4 text-green-600 dark:text-green-400" />
              <span className="text-green-600 dark:text-green-400">
                {variant === 'compact' ? 'SSO' : 'Single Sign-On'}
              </span>
            </>
          ) : (
            <>
              <KeyRound className="text-chariot-text-secondary size-4" />
              <span className="text-chariot-text-secondary">
                {variant === 'compact' ? 'Local' : 'Local Authentication'}
              </span>
            </>
          )}
        </div>
      );
    },
  });

  // Add Created column only for default variant
  if (variant !== 'compact') {
    baseColumns.push({
      label: 'Account Creation',
      id: 'updated',
      ...getColumnWidth('account', 'created', variant),
      cell: (account: AccountWithAccess) =>
        formatDate(account.updated, DATE_FORMAT, false),
    });
  }

  // Add Last Activity column (always shown)
  baseColumns.push({
    label: 'Last Activity',
    id: 'access',
    ...getColumnWidth('account', 'lastActivity', variant),
    cell: (account: AccountWithAccess) => {
      if (!account.access) {
        return <span className="text-chariot-text-tertiary">Never</span>;
      }
      return formatDate(account.access, DATE_FORMAT, false);
    },
  });

  // Add Actions column if not readOnly
  if (!readOnly) {
    baseColumns.push({
      label: 'Actions',
      id: 'config',
      ...getColumnWidth('account', 'actions', variant),
      cell: (account: AccountWithAccess) => {
        if (isAccount(account)) return null;

        return (
          <div className="flex items-center gap-2">
            <Tooltip title="Re-send invitation email">
              <IconButton
                icon={
                  isReinviting === account.member ? (
                    <div
                      className="border-chariot-stroke border-t-chariot-text-primary size-5 animate-spin rounded-full border-2"
                      role="status"
                      aria-label="Sending invitation"
                    />
                  ) : (
                    <Mail className="size-5" />
                  )
                }
                onClick={() => handleReinvite(account)}
                aria-label={`Re-invite ${account.member}`}
                variant="default"
                size="sm"
                data-testid={`reinvite-${account.member}`}
                disabled={isReinviting === account.member}
              />
            </Tooltip>
            <Tooltip title="Revoke user access">
              <IconButton
                icon={
                  <CircleX className="stroke-chariot-stroke-critical size-5" />
                }
                onClick={() => {
                  setUnlinkUser({
                    username: account.member,
                    config: account.config,
                  });
                  setShowRevokeAuthorization(true);
                }}
                aria-label={`Revoke access for ${account.member}`}
                variant="danger"
                size="sm"
                data-testid={`revoke-${account.member}`}
              />
            </Tooltip>
          </div>
        );
      },
    });
  }

  const columns = baseColumns;

  function removeUnlinkUser() {
    setShowRevokeAuthorization(false);
    setTimeout(() => {
      setUnlinkUser(null);
    }, 300);
  }

  // Show custom empty state if no users
  if (users.length === 0 && status === 'success') {
    return (
      <>
        {showHeader && (title || description) && (
          <div className="mb-4">
            {title && <h3 className="text-lg font-semibold">{title}</h3>}
            {description && (
              <p className="text-chariot-text-secondary text-sm">
                {description}
              </p>
            )}
          </div>
        )}
        <div className="border-chariot-stroke bg-chariot-background-secondary/30 rounded-lg border border-dashed p-8 text-center">
          <p className="text-chariot-text-secondary text-sm">
            No{' '}
            {tableName === 'praetorian-users'
              ? 'Praetorian team members'
              : 'customer users'}{' '}
            have been added yet
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      {showHeader && (title || description) && (
        <div className="mb-4">
          {title && <h3 className="text-lg font-semibold">{title}</h3>}
          {description && (
            <p className="text-chariot-text-secondary text-sm">{description}</p>
          )}
        </div>
      )}
      <div className="border-chariot-stroke overflow-hidden rounded-lg border">
        <TanStackTable<AccountWithAccess>
          name={tableName}
          columns={columns}
          data={users}
          error={error}
          loadingRowCount={1}
          status={status}
          noData={{
            description: `No ${title?.toLowerCase() || 'authorized users'} found.`,
          }}
          selection={{
            hide: true,
          }}
        />
      </div>
      {!readOnly && (
        <Modal
          icon={<ExclamationTriangleIcon className="size-7 text-yellow-600" />}
          title={'Revoke authorization?'}
          onClose={removeUnlinkUser}
          open={showRevokeAuthorization}
          footer={{
            text: 'Revoke',
            className: 'bg-chariot-stroke-critical',
            onClick: async () => {
              if (unlinkUser) {
                await unlink({
                  username: unlinkUser.username,
                  config: unlinkUser.config,
                });
                removeUnlinkUser();
                onUserRevoked?.();

                if (unlinkUser.username === primaryEmail) {
                  setTimeout(() => {
                    setLastVisitedUserId('');

                    queryClient.invalidateQueries({
                      queryKey: getQueryKey.getMy('account'),
                    });

                    navigate('/');
                  }, 500);
                }
              }
            },
          }}
        >
          Remove <b>{unlinkUser?.username}</b> from your account?
        </Modal>
      )}
    </>
  );
};

export interface PraetorianUsersTableProps {
  users: AccountWithAccess[];
  error?: Error | null;
  status: 'error' | 'success' | 'pending';
  customerEmail?: string;
  showHeader?: boolean;
  onUserRevoked?: () => void;
  readOnly?: boolean;
  ssoDomains?: string[];
  variant?: TableVariant;
}

export const PraetorianUsersTable: React.FC<
  PraetorianUsersTableProps
> = props => {
  const praetorianUsers = props.users.filter(user =>
    user.member?.endsWith('@praetorian.com')
  );

  return (
    <div className="border-chariot-stroke bg-chariot-background-primary/60 rounded-lg border p-6">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-600/10">
            <img src="/icons/logo.png" alt="Praetorian" className="h-6 w-6" />
          </div>
          <div>
            <h3 className="flex items-center gap-2 text-lg font-semibold">
              Praetorian Authorized Users
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                {praetorianUsers.length}
              </span>
            </h3>
            <p className="text-chariot-text-secondary mt-0.5 text-sm">
              Praetorian team members with access to this account
            </p>
          </div>
        </div>
      </div>
      <AuthorizedUsersTableBase
        {...props}
        users={praetorianUsers}
        tableName="praetorian-users"
        showHeader={false}
        variant={props.variant}
      />
    </div>
  );
};

export interface CustomerUsersTableProps {
  users: AccountWithAccess[];
  error?: Error | null;
  status: 'error' | 'success' | 'pending';
  customerEmail?: string;
  showHeader?: boolean;
  onUserRevoked?: () => void;
  readOnly?: boolean;
  ssoDomains?: string[];
  variant?: TableVariant;
}

export const CustomerUsersTable: React.FC<CustomerUsersTableProps> = props => {
  const customerUsers = props.users.filter(
    user => !user.member?.endsWith('@praetorian.com')
  );

  return (
    <div className="border-chariot-stroke bg-chariot-background-primary/60 rounded-lg border p-6">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-purple-500/10 to-purple-600/10">
            <svg
              className="h-6 w-6 text-purple-600 dark:text-purple-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <div>
            <h3 className="flex items-center gap-2 text-lg font-semibold">
              Customer Authorized Users
              <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                {customerUsers.length}
              </span>
            </h3>
            <p className="text-chariot-text-secondary mt-0.5 text-sm">
              Customer team members and collaborators with access
            </p>
          </div>
        </div>
      </div>
      <AuthorizedUsersTableBase
        {...props}
        users={customerUsers}
        tableName="customer-users"
        showHeader={false}
        variant={props.variant}
      />
    </div>
  );
};
